import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  ActionSheetController,
  ModalController,
  PopoverController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from '@panamax/app-state';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import {
  SortDirections,
  SortableSectionHeader,
} from '@shared/components/sortable-section/models/sortable-section.model';
import { ModalService } from '@shared/services/modal/modal.service';
import { CustomMessage } from '@usf/user-types/custom-message';
import { CreateCustomMessageModalComponent } from '../create-custom-message-modal/create-custom-message-modal.component';
@Component({
  selector: 'app-custom-messages-card',
  templateUrl: './custom-messages-card.component.html',
  styleUrls: ['./custom-messages-card.component.scss'],
})
export class CustomMessagesCardComponent implements OnInit {
  @Input() customMessages: CustomMessage[];
  @Input() userId: number;
  @Output() changeSort: EventEmitter<any> = new EventEmitter();
  @Output() deleteCustomMessage: EventEmitter<string> = new EventEmitter();
  @Output() downloadCustomerList: EventEmitter<any> = new EventEmitter();
  sectionHeaders: SortableSectionHeader[];
  selectedHeader = 'startDate';
  constructor(
    private modalService: ModalService,
    protected modalController: ModalController,
    readonly platformService: PlatformService,
    protected translateService: TranslateService,
    protected popoverController: PopoverController,
    protected actionSheetController: ActionSheetController,
  ) {}

  ngOnInit() {
    this.sectionHeaders = [
      {
        headerName: 'text',
        displayText: this.translateService.instant(
          'i18n.profilePage.customMessages.message',
        ),
        sortDirection: SortDirections.desc,
        size: 4,
      },
      {
        headerName: 'link',
        displayText: this.translateService.instant(
          'i18n.profilePage.customMessages.ctaURL',
        ),
        sortDirection: null,
        size: 2.5,
        offset: 0.5,
      },
      {
        headerName: 'customers',
        displayText: this.translateService.instant(
          'i18n.profilePage.customMessages.customerList',
        ),
        sortDirection: null,
        size: 1.5,
      },
      {
        headerName: 'startDate',
        displayText: this.translateService.instant(
          'i18n.profilePage.customMessages.dateRange',
        ),
        sortDirection: SortDirections.desc,
        offset: 0.5,
        size: 3,
      },
    ];
  }

  async showCreateCustomMessageModal(message?: CustomMessage) {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      ModalWithNavComponent,
      {
        rootPage: CreateCustomMessageModalComponent,
        rootPageParams: {
          message: message,
          userId: this.userId,
        },
      },
      'custom-messages-modal',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal?.present();
  }

  updateListOfMessagesSort(event: any) {
    if (event?.headerName === 'text' || event?.headerName === 'startDate') {
      this.selectedHeader = event?.headerName;
      this.sectionHeaders.find(
        s => s.headerName === event?.headerName,
      ).sortDirection = event?.sortDirection;
      this.changeSort.emit(event);
    } else {
      return false;
    }
  }

  async openOptions(event: Event, message: CustomMessage) {
    let buttons: any[] = [
      {
        text: this.translateService.instant('i18n.common.edit'),
        handler: () => {
          this.showCreateCustomMessageModal(message);
          this.dismissOptions();
        },
      },
      {
        text: this.translateService.instant('i18n.common.delete'),
        handler: () => {
          this.onDeleteCustomMessage(message);
          this.dismissOptions();
        },
      },
    ];
    if (!this.platformService.isTouch.value) {
      const optionsPopover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        event,
        componentProps: {
          buttons,
        },
        cssClass: 'custom-messages-popover-desktop',
        mode: 'ios',
        showBackdrop: false,
      });
      await optionsPopover?.present();
    } else {
      buttons.unshift({
        text: this.translateService.instant(
          'i18n.profilePage.customMessages.downloadCustomerList',
        ),
        handler: () => {
          this.onDownloadCustomerList(message);
          this.dismissOptions();
        },
      });
      buttons.push({
        text: this.translateService.instant('i18n.common.back'),
        role: 'cancel',
        cssClass: 'cancel-button',
      });
      const optionsActionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons,
      });
      await optionsActionSheet?.present();
    }
  }

  async dismissOptions() {
    if (this.platformService.isTouch.value) {
      await this.actionSheetController?.dismiss();
    } else {
      await this.popoverController?.dismiss();
    }
  }

  onDeleteCustomMessage(message: CustomMessage) {
    this.deleteCustomMessage.emit(message.messageId);
  }

  onDownloadCustomerList(message: any) {
    this.downloadCustomerList.emit(message.customers);
  }

  openCTA(link: string) {
    if (link !== '') window.open(link, '_blank');
  }
}
